import { useCallback } from "react";
import { SERVER_HOST } from "../../../constants/config";
import { getEkycHeaders } from "../../../utils/ekycHeaders";

const BaseAPIUrl = SERVER_HOST;

const FETCH_TIMEOUT_MS = 15000;

const useEKYCManagerService = () => {

  const getNextStep = useCallback(
    async (validationID) => {
      const config = {
        method: "get",
        headers: getEkycHeaders(),
      };

      return fetch(
        `${BaseAPIUrl}/ekyc/ekyc-manager/get-next-step/${validationID}`,
        config
      ).then((response) => response.json());
    },
    []
  );

  const getValidationRecordFromToken = useCallback(
    async (token) => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

      let response;
      try {
        response = await fetch(`${BaseAPIUrl}/ekyc/ekyc-manager/${token}`, {
          method: "get",
          signal: controller.signal,
        });
      } catch (err) {
        clearTimeout(timeout);
        const wrapped = new Error(err.name === "AbortError" ? "Request timed out" : "Network error");
        wrapped.isConnectionError = true;
        throw wrapped;
      }

      clearTimeout(timeout);

      if (!response.ok) {
        const err = new Error(`Server error: ${response.status}`);
        err.isConnectionError = true;
        throw err;
      }

      let result;
      try {
        result = await response.json();
      } catch (parseErr) {
        const err = new Error("Invalid response from server");
        err.isConnectionError = true;
        throw err;
      }

      if (result.status === "success") {
        sessionStorage.setItem("ekycSessionToken", token);
      }
      return result;
    },
    []
  );

  const saveDeviceIntelligence = useCallback(
    async (validationID, deviceData) => {
      const config = {
        method: "post",
        headers: getEkycHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(deviceData),
      };

      return fetch(
        `${BaseAPIUrl}/ekyc/ekyc-manager/device-intelligence/${validationID}`,
        config
      ).then((response) => response.json());
    },
    []
  );

  const getSessionInfo = useCallback(
    async (validationID) => {
      return fetch(
        `${BaseAPIUrl}/ekyc/ekyc-manager/session-info/${validationID}`,
        { method: "get", headers: getEkycHeaders() }
      ).then((response) => response.json());
    },
    []
  );

  const getStatus = useCallback(
    async (validationID) => {
      return fetch(
        `${BaseAPIUrl}/ekyc/ekyc-manager/status/${validationID}`,
        { method: "get", headers: getEkycHeaders() }
      ).then((response) => response.json());
    },
    []
  );

  const contactSupport = useCallback(
    async (validationID, payload) => {
      const config = {
        method: "post",
        headers: getEkycHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(payload || {}),
      };

      return fetch(
        `${BaseAPIUrl}/ekyc/diagnostics/${validationID}/contact-support`,
        config
      ).then((response) => response.json());
    },
    []
  );

  return {
    getNextStep,
    getValidationRecordFromToken,
    getSessionInfo,
    getStatus,
    saveDeviceIntelligence,
    contactSupport,
  };
};

export { useEKYCManagerService };
